import { Grid, Typography } from '@material-ui/core'
import { AddressField, AutoCompleteField, CityStateCombinedField, PhoneField, SelectField, TextField } from '~/components/fields'
import { FIELD_MODE, TRAVEL_EMPLOYMENT_TYPE } from '~/common/constants'
import { SuccessMarkSvg } from '~/components/icons'
import { changePhoneDisabledStyle, changePhoneStyle, helperTextStyle, labelEndStyle } from '~/features/care-profile/care-profile.style'
import { parseAddressToView, validatePhoneNumber } from '~/common/helpers'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { FormMode } from '@opus/web.core.form.utils'
import { useToggle } from 'react-use'
import { appStore, masterStore, notifyStore } from '~/stores'
import { observer } from '~/common/mobx.decorator'
import { compact, isArray } from 'lodash'
import { infoTabStore } from '~/features/care-activation/tabs/info/info-tab.store'
import { logClickEvent } from '~/common/tracking/event-client.tracking'
import { ACTIATION_TRACKING_CONSTANT } from '../care-activation.constant'
import { otpDialogStore } from '~/components/otp-dialog/otp-dialog.store'

const BasicInformationForm = observer(({ mode = FormMode.Edit, singleColumn, formValues, onVerifyPhone, showPhoneOtp, isApplyJob = false }) => {
	const { t } = useTranslation()
	const [changePhone, toggleChangePhone] = useToggle(false)
	const { phoneVerified, workerPhone } = infoTabStore
	const { jobShifts, workTypes, licenseStates } = masterStore
	const { fetchCheckRemainingOtpRequests } = otpDialogStore

	useEffect(() => {
		if (!showPhoneOtp) {
			toggleChangePhone(false)
		}
	}, [showPhoneOtp, toggleChangePhone])

	const showPreferredLocations = useMemo(() => isArray(formValues.preferredWorkTypes) && formValues.preferredWorkTypes?.includes(TRAVEL_EMPLOYMENT_TYPE), [
		formValues.preferredWorkTypes,
	])

	const newPhoneInputted = useMemo(() => (formValues.phone ?? '').replace('+1', '') !== (workerPhone ?? '').replace('+1', ''), [formValues.phone, workerPhone])

	const phoneValid = useMemo(() => formValues.phone?.length === 12 && validatePhoneNumber(formValues.phone), [formValues.phone])

	return (
		<>
			<Grid container spacing={2}>
				<Grid item xs={12} md={singleColumn ? 12 : 6}>
					<TextField
						disabled={true}
						inputProps={{ maxLength: 50 }}
						name="firstName"
						label="FIRST_NAME"
						placeholder={t('$PLACEHOLDERS.FIRST_NAME')}
						mode={mode}
					/>
					<Typography style={{ marginTop: 8, fontSize: 14, fontWeight: 500, color: appStore.getTheme.colors.content.dark }} variant="body1">
						{t('REACH_OUT_RECRUITER')}
					</Typography>
				</Grid>
				<Grid item xs={12} md={singleColumn ? 12 : 6}>
					<TextField disabled={true} name="lastName" inputProps={{ maxLength: 50 }} label="LAST_NAME" placeholder={t('$PLACEHOLDERS.LAST_NAME')} mode={mode} />
					<Typography style={{ marginTop: 8, fontSize: 14, fontWeight: 500, color: appStore.getTheme.colors.content.dark }} variant="body1">
						{t('REACH_OUT_RECRUITER')}
					</Typography>
				</Grid>

				<Grid item xs={12} md={singleColumn ? 12 : 6}>
					<TextField disabled={true} name="email" type="email" mode={mode} label="EMAIL_ADDRESS" placeholder={t('$PLACEHOLDERS.EMAIL')} />
					<Typography style={{ marginTop: 8, fontSize: 14, fontWeight: 500, color: appStore.getTheme.colors.content.dark }} variant="body1">
						{t('REACH_OUT_RECRUITER')}
					</Typography>
				</Grid>

				<Grid item xs={12} md={singleColumn ? 12 : 6}>
					<PhoneField
						autoFocus
						phoneVerified={phoneVerified}
						disabled={mode === FIELD_MODE.edit && !changePhone && phoneVerified}
						name="phone"
						mode={mode}
						label="PHONE_NUMBER"
						placeholder="$PLACEHOLDERS.PHONE"
						InputProps={{
							endAdornment: !changePhone && phoneVerified && <SuccessMarkSvg />,
						}}
						endLabel={
							!changePhone && phoneVerified ? (
								<></>
							) : (
								// <Typography
								// 	css={changePhoneStyle}
								// 	onClick={async () => {
								// 		const data = await fetchCheckRemainingChangePhone()
								// 		if (data?.phoneChangeRemaining > 0) {
								// 			logClickEvent(ACTIATION_TRACKING_CONSTANT.CLICK_CHANGE_PHONE_BTN_PROFILE_COMPLETION_PERSONAL_INFO)
								// 			toggleChangePhone(true)
								// 		}
								// 	}}
								// >
								// 	{t('CHANGE_ALT')}
								// </Typography>
								<Typography
									onClick={async () => {
										const data = await fetchCheckRemainingOtpRequests(formValues.phone)
										if (data?.remainingSessionResendCode === 0) {
											await notifyStore.open(
												'You can request the verification code up to three times a day and six times a month. Please wait until the time limit passes or contact the support team for assistance.',
												{
													title: 'Send OTP request limit reached',
													showDialog: true,
													keepOriginal: true,
													okText: 'I got it',
													cancelText: 'Cancel',
													onOk: (key) => {
														notifyStore.closeDialog(key)
													},
													onCancel: (key) => {
														notifyStore.closeDialog(key)
													},
												}
											)
										} else if (data?.remainingSessionResendCode > 0) {
											logClickEvent(ACTIATION_TRACKING_CONSTANT.CLICK_VERIFY_PHONE_BTN_PROFILE_COMPLETION_PERSONAL_INFO)
											if (!formValues.phone || (!newPhoneInputted && phoneVerified) || !phoneValid) {
												return
											}
											onVerifyPhone()
										}
									}}
									css={!formValues.phone || (!newPhoneInputted && phoneVerified) || !phoneValid ? changePhoneDisabledStyle : changePhoneStyle}
								>
									{t('VERIFY')}
								</Typography>
							)
						}
					/>

					{phoneVerified && (
						<Typography style={{ marginTop: 8, fontSize: 14, fontWeight: 500, color: appStore.getTheme.colors.content.dark }} variant="body1">
							{t('REACH_OUT_RECRUITER')}
						</Typography>
					)}
				</Grid>

				{!isApplyJob && (
					<>
						<Grid item xs={12} md={singleColumn ? 12 : 6}>
							<AddressField
								name="workerAddress"
								label="PERMANENT_ADDRESS"
								mode={mode}
								valueDisplayFormat={(data) => parseAddressToView(data, ['street', 'city', 'state', 'zipcode'])}
							/>
						</Grid>

						<Grid item xs={12} md={singleColumn ? 12 : 6}>
							<AddressField
								name="temporaryAddress"
								label="TEMPORARY_ADDRESS"
								mode={mode}
								endLabel={
									<Typography variant="subtitle2" css={labelEndStyle}>
										{t('OPTIONAL')}
									</Typography>
								}
								valueDisplayFormat={(data) => parseAddressToView(data, ['street', 'city', 'state', 'zipcode'])}
							/>
						</Grid>

						<Grid item xs={12} md={singleColumn ? 12 : 6}>
							<AutoCompleteField
								multiple={true}
								name="preferredShifts"
								label="DESIRED_SHIFTS"
								options={jobShifts}
								placeholder="$PLACEHOLDERS.DESIRED_SHIFTS"
								mode={mode}
								helperText={<Typography css={helperTextStyle}>{t('$MESSAGES.SELECT_SHIFTS')}</Typography>}
							/>
						</Grid>

						<Grid item xs={12} md={singleColumn ? 12 : 6}>
							<AutoCompleteField
								multiple={true}
								name="preferredWorkTypes"
								label="DESIRED_EMPLOYMENT_TYPES"
								placeholder="$PLACEHOLDERS.DESIRED_EMPLOYMENT_TYPES"
								options={workTypes}
								mode={mode}
								helperText={<Typography css={helperTextStyle}>{t('$MESSAGES.SELECT_EMPLOYMENT_TYPES')}</Typography>}
							/>
						</Grid>

						{showPreferredLocations && (
							<Grid item xs={12} md={singleColumn ? 12 : 6}>
								<SelectField
									name="preferredLocation"
									label={t('PRIMARY_PREFERRED_LOCATION')}
									placeholder={t('$PLACEHOLDERS.PREFERRED_LOCATIONS')}
									options={licenseStates}
									mode={mode}
								/>
							</Grid>
						)}

						{showPreferredLocations && (
							<Grid item xs={12} md={singleColumn ? 12 : 6}>
								<CityStateCombinedField
									multiple
									name="otherPreferredLocations"
									label="OTHER_PREFERRED_LOCATION"
									placeholder="$PLACEHOLDERS.OTHER_PREFERRED_LOCATIONS"
									mode={mode}
									maxItemSelected={2}
									excludedStates={compact([formValues.preferredLocation])}
									helperText={<Typography css={helperTextStyle}>{t('$MESSAGES.SELECT_LOCATIONS')}</Typography>}
								/>
							</Grid>
						)}
					</>
				)}
			</Grid>
		</>
	)
})

export default BasicInformationForm
